import { z } from 'zod';
import { idSchema, roleSchema } from './common';

/**
 * Şifre politikası.
 *
 * En az 8 karakter ve dört sınıfın hepsi: bir büyük harf, bir küçük harf, bir
 * rakam ve bir özel karakter. (Önceki politika min 10 + yalnızca harf/rakamdı;
 * stüdyo bilinçli olarak dört sınıflı bir kural istedi.)
 *
 * Büyük/küçük harf sınıfları Türkçe harfleri de kapsar (İ/ı dâhil); özel
 * karakter, harf ya da rakam OLMAYAN her şeydir — böylece bir Türkçe harf
 * yanlışlıkla "özel karakter" sayılmaz.
 *
 * Not: kural yalnızca YENİ ve DEĞİŞTİRİLEN şifreleri bağlar. Giriş yalnızca
 * bcrypt karşılaştırması yapar, dolayısıyla eski politikayla açılmış hesaplar
 * kilitlenmez.
 */
export const passwordSchema = z
  .string()
  .min(8, 'Şifre en az 8 karakter olmalıdır')
  .max(128, 'Şifre en fazla 128 karakter olabilir')
  .refine((v) => /[A-ZÇĞİÖŞÜ]/.test(v), 'Şifre en az bir büyük harf içermelidir')
  .refine((v) => /[a-zçğıöşü]/.test(v), 'Şifre en az bir küçük harf içermelidir')
  .refine((v) => /\d/.test(v), 'Şifre en az bir rakam içermelidir')
  .refine(
    (v) => /[^A-Za-z0-9ÇĞİÖŞÜçğıöşü]/.test(v),
    'Şifre en az bir özel karakter içermelidir',
  );

/**
 * E-posta alanı — tek doğrulama kaynağı.
 *
 * `trim().toLowerCase()` normalize eder (giriş kimliği büyük/küçük harfe
 * duyarlı olmamalı), `.email()` biçimi doğrular. Login, kayıt, eğitmen ve
 * yeni öğrenci formları hepsi bunu kullanır ki "geçerli e-posta" kuralı tek
 * yerde tanımlı olsun.
 */
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email('Geçerli bir e-posta giriniz');

/** Türkiye cep telefonu — E.164'e normalize edilir. */
export const phoneSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/[\s()-]/g, ''))
  .refine((v) => /^(\+90|0)?5\d{9}$/.test(v), 'Geçerli bir cep telefonu giriniz')
  .transform((v) => `+90${v.replace(/^(\+90|0)/, '')}`);

/**
 * Serbest iletişim telefonu (stüdyo vitrini, WhatsApp).
 *
 * `phoneSchema`'dan AYRI ve bilinçli: bu numara bir kullanıcı hesabı değil,
 * sitede gösterilen iletişim numarasıdır ve sabit hat (0216…) ya da
 * uluslararası bir numara olabilir. Katı cep regex'i geçerli numaraları
 * reddederdi. Yine de "gerçekten telefon mu" diye bakar: yalnızca telefon
 * karakterleri (+ boşluk () -) ve 7–15 arası rakam.
 */
export const contactPhoneSchema = z
  .string()
  .trim()
  .max(40)
  .transform((v) => (v === '' ? undefined : v))
  .optional()
  .refine((v) => {
    if (v === undefined) return true;
    if (!/^[+\d][\d\s()-]*$/.test(v)) return false;
    const digits = v.replace(/\D/g, '');
    return digits.length >= 7 && digits.length <= 15;
  }, 'Geçerli bir telefon numarası girin');

/**
 * Giriş kimliği — e-posta YA DA cep telefonu.
 *
 * Tek kutu, iki biçim: "@" içeriyorsa e-posta, içermiyorsa telefon olarak
 * çözülür ve kayıttaki biçime normalize edilir (e-posta küçük harf, telefon
 * +90…). Çıktı hangi kolona bakılacağını taşır; servis tahmin yürütmez.
 * Tanınmayan girdi, hangi biçimin beklendiğini söyleyen tek bir hatayla
 * reddedilir.
 */
export const loginIdentifierSchema = z
  .string()
  .trim()
  .min(3, 'E-posta ya da telefon giriniz')
  .max(254)
  .transform((raw, ctx) => {
    if (raw.includes('@')) {
      const email = emailSchema.safeParse(raw);
      if (email.success) return { kind: 'email' as const, value: email.data };
    } else {
      const phone = phoneSchema.safeParse(raw);
      if (phone.success) return { kind: 'phone' as const, value: phone.data };
    }
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Geçerli bir e-posta ya da cep telefonu giriniz',
    });
    return z.NEVER;
  });

export const loginSchema = z
  .object({
    identifier: loginIdentifierSchema,
    password: z.string().min(1, 'Şifre zorunludur'),
  })
  .strict();
/** Sunucunun gördüğü, çözülmüş giriş. */
export type LoginInput = z.infer<typeof loginSchema>;
/** İstemcinin gönderdiği ham giriş (kimlik düz metin). */
export type LoginRequest = z.input<typeof loginSchema>;

export const registerSchema = z
  .object({
    name: z.string().trim().min(2, 'Ad soyad en az 2 karakter').max(80),
    email: emailSchema,
    phone: phoneSchema,
    password: passwordSchema,
  })
  .strict();
export type RegisterInput = z.infer<typeof registerSchema>;

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1),
    newPassword: passwordSchema,
  })
  .strict();
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

/** "Şifremi unuttum" — yalnızca e-posta. Yanıt her zaman aynı (kullanıcı sızdırmaz). */
export const forgotPasswordSchema = z.object({ email: emailSchema }).strict();
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

/** E-postadaki jetonla yeni şifre belirleme. */
export const resetPasswordSchema = z
  .object({
    token: z.string().min(1, 'Sıfırlama bağlantısı geçersiz'),
    newPassword: passwordSchema,
  })
  .strict();
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

export const updateProfileSchema = z
  .object({
    injuryNotes: z.string().trim().max(1000).nullable().optional(),
    goalType: z.string().trim().max(200).nullable().optional(),
  })
  .strict();
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

/** İstemciye dönen kullanıcı görünümü. passwordHash / tokenVersion ASLA yer almaz. */
export interface SessionUserDto {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: z.infer<typeof roleSchema>;
  avatarUrl: string | null;
  injuryNotes: string | null;
  goalType: string | null;
  credits: { groupCredits: number; ptCredits: number };
  instructorId: string | null;
  /**
   * Personel hesabı aynı zamanda üye olarak ders alıyor.
   *
   * `role` ile BİRLİKTE okunur; tek başına "üye mi?" sorusunun cevabı değil
   * (CLIENT'ta false kalır ve o hesap yine üyedir). Karar tek yerde:
   * `isMemberAccount` (shared/roles.ts).
   */
  trainsAsMember: boolean;
  /**
   * Hesap geçici bir şifreyle açıldı ve şifre henüz değiştirilmedi.
   * true iken sunucu /auth/me ve /auth/me/password dışındaki her uca 403 döner;
   * istemci de kullanıcıyı zorunlu şifre değiştirme ekranına kilitler.
   */
  mustChangePassword: boolean;
  /**
   * Üyelik sözleşmesinin GÜNCEL sürümü imzalanmadı (yalnızca üye hesabı).
   * true iken sunucu /auth/me, /auth/me/agreement, /auth/me/password ve
   * /auth/logout dışındaki her uca 403 AGREEMENT_REQUIRED döner; istemci
   * sözleşme ekranını gösterir.
   */
  agreementRequired: boolean;
}

/**
 * Bir hesabın en son sözleşme imzası — Profilim'de üyeye, Üyeler sekmesinde
 * yöneticiye gösterilir. `isCurrent` false ise üye eski bir sürümü imzalamış;
 * bir sonraki girişte güncel metni onaylaması istenir.
 */
export interface AgreementSignatureDto {
  version: string;
  /** Üyenin (18 yaş altında velinin) imza yerine yazdığı ad soyad. */
  fullName: string;
  signedAt: string;
  isCurrent: boolean;
}

/**
 * Sözleşme imzası: üye (18 yaş altında veli) adını yazar ve onaylar. `version`
 * istemcinin GÖSTERDİĞİ metnin sürümüdür — sunucudaki güncel sürümle aynı
 * değilse imza reddedilir (üye eski bir metni onaylamış olmasın).
 */
export const signAgreementSchema = z
  .object({
    version: z.string().trim().min(1).max(40),
    fullName: z.string().trim().min(3, 'Ad soyad en az 3 karakter olmalı').max(120),
    accepted: z.literal(true, { errorMap: () => ({ message: 'Sözleşmeyi onaylamanız gerekiyor' }) }),
  })
  .strict();

export type SignAgreementInput = z.infer<typeof signAgreementSchema>;

export const jwtPayloadSchema = z.object({
  sub: idSchema,
  role: roleSchema,
  /**
   * Token'ın basıldığı andaki `User.tokenVersion`.
   *
   * Access token'da da taşınmasının nedeni iptalin ANINDA etki etmesidir:
   * bu alan olmadan, çıkış yapılmış veya şifresi değiştirilmiş bir hesabın
   * access token'ı süresi dolana kadar (15 dk) geçerli kalıyordu.
   */
  tv: z.number().int().nonnegative(),
});
export type JwtPayload = z.infer<typeof jwtPayloadSchema>;
